import { damp, dist, lerpAngle } from '../shared/math';
import { generateWorld, ObstacleGrid } from '../shared/world';
import type {
  DropSnapshot,
  EnemySnapshot,
  LeaderboardEntry,
  PlayerSnapshot,
  ProjectileSnapshot,
} from '../shared/protocol';
import type { Obstacle, PointOfInterest, WorldInfo } from '../shared/types';

export interface ViewPlayer {
  id: number;
  snap: PlayerSnapshot;
  x: number;
  y: number;
  aim: number;
  /** Free-running animation clock, so identical animations desync between entities. */
  anim: number;
  /** Counts down after an attack, driving the swing animation. */
  swing: number;
  /** Counts down after taking damage. */
  hitFlash: number;
  /** Smoothed movement flag for the walk bob. */
  moving: number;
}

export interface ViewEnemy {
  id: number;
  snap: EnemySnapshot;
  x: number;
  y: number;
  aim: number;
  anim: number;
  hitFlash: number;
  moving: number;
}

export interface ViewProjectile {
  id: number;
  snap: ProjectileSnapshot;
  x: number;
  y: number;
  /** Seconds since the newest snapshot for this projectile. */
  age: number;
}

/** Loot on the ground. Static, so it just needs a spawn "pop" animation. */
export interface ViewDrop {
  id: number;
  snap: DropSnapshot;
  x: number;
  y: number;
  pop: number;
}

/**
 * Holds every networked entity with its smoothed render transform.
 * The server sends 20 snapshots per second; we render at the display rate.
 */
export class WorldView {
  world: WorldInfo | null = null;
  /**
   * Scenery is generated locally from the same seed the server uses rather
   * than shipped over the wire - the world is far too big to send, and the
   * deterministic generator guarantees both sides agree.
   */
  obstacles: Obstacle[] = [];
  grid: ObstacleGrid | null = null;
  /** Landmarks, so quests can name and point at them. */
  landmarks: PointOfInterest[] = [];
  /** Where the active quest wants us to go, if anywhere. */
  navTarget: { x: number; y: number; name: string } | null = null;
  /** Ids of players in our party, so they can be picked out on screen. */
  partyIds = new Set<number>();
  /** Names carved into the monument. */
  leaderboard: LeaderboardEntry[] = [];
  selfId = 0;

  players = new Map<number, ViewPlayer>();
  enemies = new Map<number, ViewEnemy>();
  projectiles = new Map<number, ViewProjectile>();
  drops = new Map<number, ViewDrop>();

  setWorld(info: WorldInfo): void {
    this.world = info;
    const generated = generateWorld();
    this.obstacles = generated.obstacles;
    this.landmarks = generated.pointsOfInterest;
    this.grid = new ObstacleGrid(this.obstacles, info.width);
  }

  reset(): void {
    this.players.clear();
    this.enemies.clear();
    this.projectiles.clear();
    this.drops.clear();
    this.world = null;
    this.obstacles = [];
    this.grid = null;
    this.landmarks = [];
    this.navTarget = null;
    this.selfId = 0;
  }

  get me(): ViewPlayer | undefined {
    return this.players.get(this.selfId);
  }

  applyPlayers(snaps: PlayerSnapshot[]): void {
    const seen = new Set<number>();
    for (const snap of snaps) {
      seen.add(snap.id);
      let view = this.players.get(snap.id);
      if (!view) {
        view = {
          id: snap.id,
          snap,
          x: snap.x,
          y: snap.y,
          aim: snap.aim,
          anim: Math.random() * 10,
          swing: 0,
          hitFlash: 0,
          moving: 0,
        };
        this.players.set(snap.id, view);
      } else {
        // A fresh attack (cooldown jumped up) restarts the swing animation.
        if (snap.cd > view.snap.cd + 0.05) view.swing = 1;
        view.snap = snap;
      }
    }
    for (const id of [...this.players.keys()]) {
      if (!seen.has(id)) this.players.delete(id);
    }
  }

  applyEnemies(snaps: EnemySnapshot[]): void {
    const seen = new Set<number>();
    for (const snap of snaps) {
      seen.add(snap.id);
      const existing = this.enemies.get(snap.id);
      if (existing) {
        existing.snap = snap;
        continue;
      }
      this.enemies.set(snap.id, {
        id: snap.id,
        snap,
        x: snap.x,
        y: snap.y,
        aim: snap.aim,
        anim: Math.random() * 10,
        hitFlash: 0,
        moving: 0,
      });
    }
    for (const id of [...this.enemies.keys()]) {
      if (!seen.has(id)) this.enemies.delete(id);
    }
  }

  applyProjectiles(snaps: ProjectileSnapshot[]): void {
    const seen = new Set<number>();
    for (const snap of snaps) {
      seen.add(snap.id);
      const existing = this.projectiles.get(snap.id);
      if (existing) {
        existing.snap = snap;
        existing.age = 0;
        continue;
      }
      this.projectiles.set(snap.id, { id: snap.id, snap, x: snap.x, y: snap.y, age: 0 });
    }
    for (const id of [...this.projectiles.keys()]) {
      if (!seen.has(id)) this.projectiles.delete(id);
    }
  }

  applyDrops(snaps: DropSnapshot[]): void {
    const seen = new Set<number>();
    for (const snap of snaps) {
      seen.add(snap.id);
      const existing = this.drops.get(snap.id);
      if (existing) {
        existing.snap = snap;
        continue;
      }
      this.drops.set(snap.id, { id: snap.id, snap, x: snap.x, y: snap.y, pop: 1 });
    }
    for (const id of [...this.drops.keys()]) {
      if (!seen.has(id)) this.drops.delete(id);
    }
  }

  /** Advance the render transforms. `dt` is in seconds. */
  update(dt: number): void {
    const smooth = damp(16, dt);
    const aimSmooth = damp(18, dt);

    for (const player of this.players.values()) {
      player.anim += dt;
      player.swing = Math.max(0, player.swing - dt * 3.4);
      player.hitFlash = Math.max(0, player.hitFlash - dt * 3);
      if (player.id === this.selfId) continue; // predicted locally

      const dead = player.snap.state === 'dead';
      if (dead) {
        player.x = player.snap.x;
        player.y = player.snap.y;
        player.moving = 0;
        continue;
      }
      const moved = dist(player.x, player.y, player.snap.x, player.snap.y);
      const factor = moved > 240 ? 1 : smooth;
      player.x += (player.snap.x - player.x) * factor;
      player.y += (player.snap.y - player.y) * factor;
      player.aim = lerpAngle(player.aim, player.snap.aim, aimSmooth);
      const target = moved > 2 ? 1 : 0;
      player.moving += (target - player.moving) * damp(10, dt);
    }

    for (const enemy of this.enemies.values()) {
      enemy.anim += dt;
      enemy.hitFlash = Math.max(0, enemy.hitFlash - dt * 3);
      const moved = dist(enemy.x, enemy.y, enemy.snap.x, enemy.snap.y);
      const factor = moved > 300 ? 1 : smooth;
      enemy.x += (enemy.snap.x - enemy.x) * factor;
      enemy.y += (enemy.snap.y - enemy.y) * factor;
      enemy.aim = lerpAngle(enemy.aim, enemy.snap.aim, aimSmooth);
      const target = moved > 2 ? 1 : 0;
      enemy.moving += (target - enemy.moving) * damp(10, dt);
    }

    for (const shot of this.projectiles.values()) {
      shot.age += dt;
      // Extrapolate: projectiles travel in a straight line at a known speed.
      const speed = shot.snap.speed;
      const ex = shot.snap.x + Math.cos(shot.snap.aim) * speed * shot.age;
      const ey = shot.snap.y + Math.sin(shot.snap.aim) * speed * shot.age;
      const factor = damp(34, dt);
      shot.x += (ex - shot.x) * factor;
      shot.y += (ey - shot.y) * factor;
    }

    for (const drop of this.drops.values()) {
      drop.pop = Math.max(0, drop.pop - dt * 2.6);
    }
  }

  /** Mark an entity as freshly hit, for the white flash. */
  flashPlayer(id: number): void {
    const player = this.players.get(id);
    if (player) player.hitFlash = 1;
  }

  flashEnemy(id: number): void {
    const enemy = this.enemies.get(id);
    if (enemy) enemy.hitFlash = 1;
  }

  /** Nearest living monster, used for the target reticle. */
  nearestEnemy(x: number, y: number, maxDistance: number): ViewEnemy | null {
    let best: ViewEnemy | null = null;
    let bestDist = maxDistance * maxDistance;
    for (const enemy of this.enemies.values()) {
      const dx = enemy.x - x;
      const dy = enemy.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) {
        bestDist = d2;
        best = enemy;
      }
    }
    return best;
  }
}
